import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ExternalLink, Link2, Trash2, CalendarClock, Cloud, Send, Download } from "lucide-react";
import { api } from "@/lib/api";
import { PIPELINE_COLUMNS, TYPE_LABEL, columnOfStatus } from "@/lib/constants";
import { FlagBadges } from "@/components/Badges";
import { downloadMediaFiles, mediaDownloadUrls } from "@/lib/mediaDownload";
import { FileUpload, resolveMediaUrl, replaceUploadsInForm } from "@/components/FileUpload";
import { SharePointPickerDialog } from "@/components/SharePointPickerDialog";
import { AutoTextarea } from "@/components/AutoTextarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function ContentDrawer({ item, onClose, onChanged }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [spOpen, setSpOpen] = useState(false);
  const [dlPct, setDlPct] = useState(null);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const initializedItemId = useRef(null);

  useEffect(() => {
    api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (item?.account_key)
      api.get("/social-groups", { params: { account: item.account_key } }).then(({ data }) => setGroups(data)).catch(() => {});
  }, [item?.account_key]);

  useEffect(() => {
    // Die Pipeline aktualisiert den ausgewaehlten Beitrag alle 15 Sekunden.
    // Einen bereits geoeffneten Entwurf dabei nicht mit dem alten Serverstand
    // ueberschreiben — besonders nicht waehrend eines grossen Video-Uploads.
    if (!item) {
      initializedItemId.current = null;
      return;
    }
    if (initializedItemId.current !== item.id) {
      initializedItemId.current = item.id;
      setForm({
        title: item.title, hook: item.hook, caption: item.caption, cta: item.cta,
        assigned_creator: item.assigned_creator, status: item.status, scheduled_date: item.scheduled_date || "",
        source_link: item.source_link || "", video_url: item.video_url || "", media_url: item.media_url || "",
        carousel_urls: item.carousel_urls || [], content_type: item.content_type,
        relevant_groups: item.relevant_groups || [],
      });
    }
  }, [item]);

  const replaceUploadedMedia = async (items) => {
    const previous = form;
    const next = replaceUploadsInForm(previous, items);
    setForm(next);
    try {
      await api.patch(`/content/${item.id}`, {
        video_url: next.video_url,
        media_url: next.media_url,
        carousel_urls: next.carousel_urls,
        content_type: next.content_type,
      });
      onChanged?.();
    } catch (error) {
      setForm(previous);
      throw error;
    }
  };

  if (!item || !form) return null;
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const minScheduleDate = (() => {
    const date = new Date(Date.now() + 60_000);
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
  })();

  const deleteItem = async () => {
    setDeleting(true);
    try {
      await api.delete(`/content/${item.id}`);
      toast.success("Beitrag gelöscht");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeleting(false);
    }
  };

  const runAI = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/suggest", {
        account_key: item.account_key, title: form.title || item.title,
        summary: form.caption || form.hook || "", content_type: item.content_type,
      });
      setForm((s) => ({ ...s, hook: data.hook, caption: data.caption, cta: data.cta }));
      toast.success(data.data_backed ? "KI-Vorschläge auf Basis echter Performance-Daten eingefügt" : "KI-Vorschläge eingefügt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
    } finally {
      setAiBusy(false);
    }
  };

  const save = async () => {
    if (form.scheduled_date && new Date(form.scheduled_date).getTime() <= Date.now()) {
      toast.error("Die geplante Uhrzeit liegt bereits in der Vergangenheit. Bitte eine zukünftige Uhrzeit wählen.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, scheduled_date: form.scheduled_date || null });
      if (mediaWarn) toast.warning("⚠️ Kein Bild/Video angehängt — die geplante Instagram-Veröffentlichung wird fehlschlagen");
      toast.success("Gespeichert");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    setSaving(true);
    try {
      await api.patch(`/content/${item.id}`, { ...form, status: "review", scheduled_date: form.scheduled_date || null });
      window.dispatchEvent(new Event("approvals:changed"));
      toast.success("Zur Freigabe eingereicht — die Redaktion wurde benachrichtigt 🔎");
      onChanged?.();
      onClose();
    } catch {
      toast.error("Einreichen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const cancelSchedule = async () => {
    setSaving(true);
    try {
      await api.delete(`/content/${item.id}/schedule-meta`);
      toast.success("Planung abgebrochen — zurück auf „Freigegeben“");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abbrechen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const sched = item.meta_schedule;
  const pub = item.meta_publish;
  const pubAcc = pub?._account;
  const storedPubRows = pub
    ? [
        ["Instagram", pub.instagram, pubAcc?.ig ? `@${pubAcc.ig}` : null],
        ["Facebook", pub.facebook, pubAcc?.fb_page || null],
      ].filter(([, r]) => r)
    : [];
  // Nach einer bewussten Neuplanung gehören vorherige Fehler nicht zum neuen
  // Lauf und dürfen nicht unter dem aktiven Termin stehen.
  const pubRows = ["published", "schedule_failed"].includes(item.status) ? storedPubRows : [];
  const pubUncertain = pubRows.some(([, r]) => r?.uncertain);
  const showSchedule = Boolean(sched) && ["scheduled", "publishing", "schedule_failed"].includes(item.status);
  const noMedia = !form.video_url && !form.media_url && !(form.carousel_urls || []).length;
  const mediaWarn = noMedia && form.scheduled_date && ["approved", "scheduled", "schedule_failed"].includes(form.status);

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        data-testid="content-drawer"
        className="w-full sm:max-w-md overflow-y-auto p-0"
      >
        <div className="relative bg-neutral-900">
          {form.video_url ? (
            <video src={resolveMediaUrl(form.video_url)} controls preload="metadata" playsInline className="h-44 w-full object-contain bg-black" />
          ) : (
            form.media_url && <img src={resolveMediaUrl(form.media_url)} alt="" className="h-44 w-full object-cover opacity-80" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 to-transparent pointer-events-none" />
          {mediaDownloadUrls(form).length > 0 && (
            <button
              data-testid="drawer-download-btn"
              onClick={async () => {
                if (dlPct !== null) return;
                setDlPct(0);
                try { await downloadMediaFiles({ urls: mediaDownloadUrls(form), title: item.title, onProgress: setDlPct }); }
                finally { setDlPct(null); }
              }}
              disabled={dlPct !== null}
              className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full bg-white/90 text-neutral-900 backdrop-blur hover:bg-white transition disabled:opacity-70"
            >
              {dlPct !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {dlPct !== null ? `${dlPct}%` : mediaDownloadUrls(form).length > 1 ? `Alle Medien (${mediaDownloadUrls(form).length}) als ZIP` : "Download"}
            </button>
          )}
          <div className="absolute bottom-3 left-4 pointer-events-none">
            <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">{TYPE_LABEL[item.content_type]}</span>
            <h3 className="font-head font-semibold text-white text-lg leading-tight">{item.title}</h3>
          </div>
        </div>

        <SheetHeader className="px-5 pt-4 pb-0">
          <SheetTitle className="sr-only">Inhalt bearbeiten</SheetTitle>
        </SheetHeader>

        <div className="p-5 space-y-3.5">
          {item.flags?.length > 0 && <FlagBadges flags={item.flags} size="md" />}

          {showSchedule && (
            <div data-testid="schedule-info-banner" className={`rounded-lg border p-3 text-sm ${sched.manual_check_required ? "border-amber-300 bg-amber-50" : item.status === "schedule_failed" ? "border-rose-200 bg-rose-50" : "border-teal-200 bg-teal-50"}`}>
              <div className="flex items-center gap-1.5 font-semibold text-neutral-800">
                <CalendarClock className="h-4 w-4" />
                {sched.manual_check_required ? "Veröffentlichungsstatus bei Meta prüfen" : item.status === "schedule_failed" ? "Geplante Veröffentlichung fehlgeschlagen" : "Für Instagram & Facebook eingeplant"}
              </div>
              <p className="text-neutral-600 mt-1 text-[13px]">
                {new Date(sched.publish_at).toLocaleString("de-DE", { dateStyle: "full", timeStyle: "short" })} Uhr
              </p>
              {sched.last_error && <p className={`${sched.manual_check_required ? "text-amber-700" : "text-rose-600"} mt-1 text-[12px]`}>{sched.last_error}</p>}
              <button data-testid="drawer-cancel-schedule" onClick={cancelSchedule} disabled={saving}
                className="mt-2 text-[13px] font-medium text-rose-600 hover:underline disabled:opacity-50">
                Planung abbrechen
              </button>
            </div>
          )}

          {item.status === "published" && pubRows.length === 0 && (
            <div data-testid="manual-published-hint" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-800 leading-snug">
              ℹ️ <b>Manuell als veröffentlicht markiert.</b> Dieser Beitrag wurde NICHT über die App auf Instagram/Facebook gepostet (kein Veröffentlichungs-Protokoll vorhanden) — z. B. weil die Karte per Hand in die Spalte „Veröffentlicht" gezogen wurde.
            </div>
          )}

          {pubRows.length > 0 && (
            <div data-testid="publish-log-banner" className={`rounded-lg border p-3 text-sm ${pubUncertain ? "border-amber-300 bg-amber-50" : "border-neutral-200 bg-neutral-50"}`}>
              <div className="flex items-center gap-1.5 font-semibold text-neutral-800">
                <Send className="h-4 w-4" /> Veröffentlichungs-Protokoll
              </div>
              {item.published_at && (
                <p className="text-neutral-600 mt-1 text-[13px]">
                  {new Date(item.published_at).toLocaleString("de-DE", { dateStyle: "full", timeStyle: "short" })} Uhr
                </p>
              )}
              <div className="mt-1.5 space-y-1">
                {pubRows.map(([platform, r, acc]) => (
                  <div key={platform} data-testid={`publish-log-${platform.toLowerCase()}`} className="flex items-start gap-1.5 text-[13px]">
                    <span className="shrink-0">{r.ok ? "✅" : r.uncertain ? "⚠️" : "❌"}</span>
                    <span className="text-neutral-700">
                      <b>{platform}</b>
                      {r.ok && acc ? <> → {acc}</> : null}
                      {!r.ok && <span className={r.uncertain ? "text-amber-700" : "text-rose-600"}> — {r.error || "fehlgeschlagen"}</span>}
                      {r.duplicate_prevented && <span className="text-emerald-700"> · vorhandener Beitrag erkannt</span>}
                    </span>
                  </div>
                ))}
              </div>
              {pubAcc?.account_key && <p className="text-neutral-400 mt-1.5 text-[11px]">Kanal: {pubAcc.account_key}</p>}
            </div>
          )}

          {mediaWarn && (
            <div data-testid="drawer-media-warning" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-800 leading-snug">
              ⚠️ <b>Kein Bild/Video angehängt.</b> Die automatische Veröffentlichung (Instagram & Facebook) zum Termin wird fehlschlagen — bitte Medien hochladen oder aus SharePoint wählen.
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <FileUpload
              accept="video/*,image/*"
              label="Medien hochladen"
              folderTitle={item?.title || "Allgemein"}
              multiple
              onUploadStart={() => setMediaUploading(true)}
              onUploadEnd={() => setMediaUploading(false)}
              onBatchUploaded={replaceUploadedMedia}
            />
            {mediaUploading && (
              <span className="text-xs font-medium text-blue-700">
                Neues Medium wird hochgeladen – die bisherige Datei bleibt bis zum Abschluss unverändert.
              </span>
            )}
            <button
              type="button"
              data-testid="drawer-sharepoint-btn"
              onClick={() => setSpOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-lg bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition"
            >
              <Cloud className="h-4 w-4" /> SharePoint
            </button>
            <button
              data-testid="drawer-ai"
              onClick={runAI}
              disabled={aiBusy}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90 transition disabled:opacity-60"
            >
              {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              KI
            </button>
          </div>

          {(form.carousel_urls || []).length > 0 && (
            <div data-testid="drawer-carousel-info" className="flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-[13px] text-violet-800">
              <span>🖼️ <b>{form.carousel_urls.length} Medien</b> im Karussell — Reihenfolge &amp; Entfernen im Freigabe-Center</span>
              <button
                data-testid="drawer-carousel-clear"
                onClick={() => setForm((s) => ({ ...s, carousel_urls: [], media_url: "", content_type: s.content_type === "carousel" ? "post" : s.content_type }))}
                className="text-[12px] font-semibold text-violet-600 hover:underline shrink-0 ml-2"
              >
                Leeren
              </button>
            </div>
          )}

          <Field label="Titel">
            <input data-testid="drawer-title" value={form.title} onChange={(e) => f("title", e.target.value)} className={cls} />
          </Field>
          <Field label="Status">
            <select data-testid="drawer-status" value={columnOfStatus(form.status).key} onChange={(e) => f("status", PIPELINE_COLUMNS.find((c) => c.key === e.target.value).drop)} className={`${cls} bg-white`}>
              {PIPELINE_COLUMNS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          {groups.length > 0 && (
            <Field label="Relevante Gruppen (Facebook/Instagram)">
              <div className="flex flex-wrap gap-1.5" data-testid="drawer-groups">
                {groups.map((g) => {
                  const sel = (form.relevant_groups || []).includes(g.name);
                  return (
                    <label key={g.id} data-testid={`drawer-group-${g.id}`} className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-semibold cursor-pointer transition ${sel ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-neutral-200 text-neutral-500 hover:border-violet-300"}`}>
                      <input type="checkbox" className="hidden" checked={sel} onChange={() => f("relevant_groups", sel ? (form.relevant_groups || []).filter((x) => x !== g.name) : [...(form.relevant_groups || []), g.name])} />
                      {g.name}
                    </label>
                  );
                })}
              </div>
            </Field>
          )}

          {!["review", "approved", "scheduled", "schedule_failed", "published"].includes(form.status) && (
            <button
              data-testid="drawer-submit-review"
              onClick={submitForReview}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold transition disabled:opacity-50"
            >
              <Send className="h-4 w-4" /> Zur Freigabe einreichen
            </button>
          )}
          {form.status === "review" && (
            <p data-testid="drawer-review-hint" className="text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              🔎 Wartet auf Freigabe — im Freigabe-Center prüfen und posten/planen
            </p>
          )}
          <Field label="Content-Link (z. B. OneDrive)">
            <div className="flex gap-2">
              <input data-testid="drawer-source-link" value={form.source_link || ""} onChange={(e) => f("source_link", e.target.value)} placeholder="https://onedrive.live.com/…" className={cls} />
              {form.source_link && (
                <a href={form.source_link} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-1.5 text-sm font-medium px-3 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </Field>
          <Field label="Hook">
            <input value={form.hook} onChange={(e) => f("hook", e.target.value)} className={cls} />
          </Field>
          <Field label="Caption">
            <AutoTextarea
              data-testid="drawer-caption"
              value={form.caption}
              onChange={(v) => f("caption", v)}
              minRows={5}
              className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
            />
          </Field>
          <Field label="CTA">
            <input value={form.cta} onChange={(e) => f("cta", e.target.value)} className={cls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Creator">
              <select
                data-testid="drawer-creator-select"
                value={form.assigned_creator || ""}
                onChange={(e) => f("assigned_creator", e.target.value)}
                className={`${cls} bg-white`}
              >
                <option value="">— Niemand —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.name}>{m.name}{m.role === "admin" ? " (Admin)" : ""}</option>
                ))}
                {form.assigned_creator && !members.some((m) => m.name === form.assigned_creator) && (
                  <option value={form.assigned_creator}>{form.assigned_creator}</option>
                )}
              </select>
            </Field>
            <Field label="Geplant am">
              <input type="datetime-local" data-testid="drawer-scheduled-date" min={minScheduleDate} value={form.scheduled_date || ""} onChange={(e) => f("scheduled_date", e.target.value)} className={cls} />
            </Field>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-neutral-100 p-4 flex gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="drawer-delete" title="Beitrag löschen"
                className="shrink-0 inline-flex items-center justify-center px-3 py-2.5 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition">
                <Trash2 className="h-4 w-4" />
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Beitrag löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  „{item.title}" wird endgültig aus der Pipeline entfernt. Das kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction data-testid="drawer-delete-confirm" onClick={deleteItem} disabled={deleting} className="bg-rose-600 hover:bg-rose-700">
                  Endgültig löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <button onClick={onClose} className="flex-1 text-sm font-medium px-4 py-2.5 rounded-lg border border-neutral-200 hover:bg-neutral-50">Schließen</button>
          <button data-testid="drawer-save" onClick={save} disabled={saving} className="flex-1 text-sm font-medium px-4 py-2.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50">Speichern</button>
        </div>

        <SharePointPickerDialog
          open={spOpen}
          onClose={() => setSpOpen(false)}
          onPicked={(d) => {
            setForm((s) => replaceUploadsInForm(s, [d]));
            toast.success(`„${d.filename}" aus SharePoint übernommen`);
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
